import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import PortfolioPieceEditor from "@/components/PortfolioPieceEditor";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X, Save, Upload, Globe, Linkedin, CreditCard, Trash2, AlertTriangle, PauseCircle, PlayCircle, Mail, Loader2 } from "lucide-react";
import AccountActionModal from "@/components/AccountActionModal";
import GotAJobCelebration from "@/components/GotAJobCelebration";

const DISCIPLINES = [
  "graphic_designer", "copywriter", "art_director", "photographer",
  "ux_designer", "illustrator", "brand_designer", "other",
];

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("basics");

  useEffect(() => { (async () => {
    const res = await api.get("/profile");
    setProfile(res.data);
  })(); }, []);

  if (!profile) return <Layout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></Layout>;

  const save = async (patch) => {
    setBusy(true);
    try {
      const res = await api.put("/profile", patch);
      setProfile(res.data);
      toast.success("Saved");
    } catch (err) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const tabs = ["basics", "experience", "portfolio", "voice", "preferences"];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-6 sm:py-10">
        <div className="terminal-label text-primary">[ PROFILE.SETTINGS ]</div>
        <h1 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight mt-2 mb-6 sm:mb-8">Profile</h1>

        <div className="flex md:grid md:grid-cols-5 gap-px mb-6 sm:mb-8 border border-border overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0">
          {tabs.map((t) => (
            <button
              key={t}
              data-testid={`profile-tab-${t}`}
              onClick={() => setTab(t)}
              className={`flex-shrink-0 md:flex-shrink p-3 text-[10px] uppercase tracking-[0.2em] transition-colors whitespace-nowrap ${tab === t ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "basics" && <Basics profile={profile} save={save} busy={busy} />}
        {tab === "experience" && <Experience profile={profile} save={save} busy={busy} />}
        {tab === "portfolio" && <Portfolio profile={profile} save={save} busy={busy} setProfile={setProfile} />}
        {tab === "voice" && <Voice profile={profile} setProfile={setProfile} />}
        {tab === "preferences" && <Preferences profile={profile} save={save} busy={busy} />}

        <AccountSection />
      </div>
    </Layout>
  );
}

function EmailPreferencesModal({ open, onClose }) {
  const CATEGORIES = [
    { key: "high_match_alerts", label: "High-match job alerts", desc: "Instant email when a 90%+ match lands on your feed." },
    { key: "weekly_digest", label: "Weekly digest", desc: "One email a week summarizing your top new matches." },
    { key: "feature_updates", label: "Feature updates", desc: "When we ship something new that affects your workflow." },
    { key: "announcements", label: "Announcements", desc: "Occasional milestones, press, and platform news." },
    { key: "tips_and_guides", label: "Tips and guides", desc: "Case studies + tactical advice on landing creative work." },
  ];
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/profile/email-preferences");
        if (alive) setPrefs(r.data.preferences || {});
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || "Failed to load email preferences.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const toggle = async (key) => {
    if (!prefs) return;
    const next = !prefs[key];
    const optimistic = { ...prefs, [key]: next };
    setPrefs(optimistic);
    setSavingKey(key);
    try {
      const r = await api.put("/profile/email-preferences", { [key]: next });
      setPrefs(r.data.preferences || optimistic);
    } catch (e) {
      setPrefs(prefs);
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally {
      setSavingKey("");
    }
  };

  const unsubAll = async () => {
    if (!prefs) return;
    if (!window.confirm("Turn off every marketing email? You'll still receive transactional emails (verification, password resets, receipts).")) return;
    setSavingKey("__all__");
    try {
      const off = Object.fromEntries(CATEGORIES.map((c) => [c.key, false]));
      const r = await api.put("/profile/email-preferences", off);
      setPrefs(r.data.preferences || off);
      toast.success("You're off all marketing emails.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSavingKey(""); }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/85 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="email-preferences-modal"
    >
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-background border border-foreground/30">
        <div className="flex items-start justify-between p-6 border-b border-foreground/15">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <div className="terminal-label text-primary">[ EMAIL.PREFERENCES ]</div>
            </div>
            <h3 className="font-display text-2xl uppercase font-semibold tracking-tight">Manage email preferences</h3>
            <p className="text-xs text-muted-foreground mt-2 max-w-lg">
              Toggles save instantly. Transactional emails (verification, password reset, receipts) always send regardless of these settings.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="email-prefs-close"
            className="text-muted-foreground hover:text-foreground p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading preferences...
          </div>
        ) : prefs ? (
          <div className="divide-y divide-border">
            {CATEGORIES.map((c) => {
              const on = !!prefs[c.key];
              const saving = savingKey === c.key;
              return (
                <div key={c.key} className="p-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{c.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{c.desc}</div>
                  </div>
                  <button
                    onClick={() => toggle(c.key)}
                    disabled={saving}
                    data-testid={`email-pref-toggle-${c.key}`}
                    aria-pressed={on}
                    className={`shrink-0 relative w-12 h-6 border transition-colors ${on ? "bg-primary border-primary" : "bg-background border-foreground/40"} ${saving ? "opacity-50" : ""}`}
                    title={on ? "On - click to turn off" : "Off - click to turn on"}
                  >
                    <span
                      className={`absolute top-[2px] w-4 h-4 border transition-all ${on ? "left-[26px] bg-primary-foreground border-primary-foreground" : "left-[2px] bg-foreground border-foreground/40"}`}
                    />
                    {saving && (
                      <Loader2 className="w-3 h-3 animate-spin absolute -right-6 top-1.5 text-muted-foreground" />
                    )}
                  </button>
                </div>
              );
            })}
            <div className="p-4 flex justify-end">
              <button
                onClick={unsubAll}
                disabled={savingKey === "__all__"}
                data-testid="email-pref-unsub-all"
                className="text-[11px] uppercase tracking-[0.22em] font-mono text-muted-foreground hover:text-primary inline-flex items-center gap-2"
              >
                {savingKey === "__all__" && <Loader2 className="w-3 h-3 animate-spin" />}
                Unsubscribe from all marketing emails
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AccountSection() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [billingBusy, setBillingBusy] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'pause' | 'delete' | null
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(null); // { name } when "got a job"
  const [emailPrefsOpen, setEmailPrefsOpen] = useState(false);

  const paused = !!user?.paused;

  const onManageBilling = async () => {
    setBillingBusy(true);
    try {
      const res = await api.post("/billing/portal");
      if (res.data?.available && res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.info(res.data?.message || "Billing self-service is not yet enabled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reach the billing portal.");
    } finally { setBillingBusy(false); }
  };

  const onUnpause = async () => {
    setBusy(true);
    try {
      await api.post("/auth/unpause");
      toast.success("Welcome back. Your feed is live again.");
      await refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unpause.");
    } finally { setBusy(false); }
  };

  const submitAction = async ({ reason, note, celebrate: shouldCelebrate }) => {
    setBusy(true);
    const body = { reason, note };
    try {
      if (modalMode === "delete") {
        await api.delete("/auth/account", { data: body });
        setModalMode(null);
        if (shouldCelebrate) {
          // Show celebration BEFORE the goodbye redirect
          setCelebrate({ name: "" });
          // logout fires in onCelebrationDone
          return;
        }
        toast.success("Your account has been permanently deleted.");
        logout();
        navigate("/?deleted=1", { replace: true });
      } else if (modalMode === "pause") {
        await api.post("/auth/pause", body);
        setModalMode(null);
        if (shouldCelebrate) {
          setCelebrate({ name: "" });
          await refresh?.();
          return;
        }
        toast.success("Account paused. We'll be here when you're ready.");
        await refresh?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const onCelebrationDone = () => {
    const wasDelete = !user; // user is cleared after delete already? no - user still set
    // Determine post-celebration route: deleted users get logged out + sent home; paused users return to profile.
    const wasPaused = user?.paused;
    setCelebrate(null);
    if (wasPaused) {
      // user paused - they're still logged in
      return;
    }
    // Otherwise account was deleted
    toast.success("Wishing you all the best. Go knock it out of the park.");
    logout();
    navigate("/?deleted=1", { replace: true });
  };

  return (
    <div className="mt-12 pt-10 border-t border-border" data-testid="account-section">
      <div className="terminal-label text-primary mb-2">[ ACCOUNT ]</div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight mb-6">Account</h2>

      {paused && (
        <div className="panel p-5 mb-5 border-accent-amber/40" data-testid="paused-banner" style={{ borderColor: "hsl(var(--accent-amber) / 0.4)" }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="terminal-label text-accent-amber mb-1">[ ACCOUNT.PAUSED ]</div>
              <p className="text-sm text-foreground/85 leading-relaxed max-w-xl">
                Your account is paused. Feed, AI generation, and sending are frozen. Your profile and any portfolio URLs you've already shared stay live.
              </p>
            </div>
            <button
              data-testid="unpause-btn"
              onClick={onUnpause}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-2 text-[10px] shrink-0"
            >
              <PlayCircle className="w-3 h-3" /> Resume account
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="panel p-5">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-foreground/70" strokeWidth={1.5} />
            <div className="terminal-label">Billing</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Manage your subscription, update payment details, or cancel.
          </p>
          <button
            data-testid="manage-billing-btn"
            onClick={onManageBilling}
            disabled={billingBusy}
            className="btn-secondary inline-flex items-center gap-2 text-[10px]"
          >
            {billingBusy ? "..." : "Manage billing →"}
          </button>
        </div>

        <div className="panel p-5" data-testid="email-preferences-box">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-foreground/70" strokeWidth={1.5} />
            <div className="terminal-label">Email preferences</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Choose which emails you get from Sisyphean. Toggles save instantly.
          </p>
          <button
            data-testid="manage-email-prefs-btn"
            onClick={() => setEmailPrefsOpen(true)}
            className="btn-secondary inline-flex items-center gap-2 text-[10px]"
          >
            Manage preferences →
          </button>
        </div>

        {!paused && (
          <div className="panel p-5 border-accent-amber/40" style={{ borderColor: "hsl(var(--accent-amber) / 0.4)" }}>
            <div className="flex items-center gap-2 mb-3">
              <PauseCircle className="w-4 h-4 text-accent-amber" strokeWidth={1.5} />
              <div className="terminal-label text-accent-amber">Pause account</div>
            </div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Freeze the feed + generation while keeping your profile, portfolios, and history intact.
            </p>
            <button
              data-testid="pause-account-btn"
              onClick={() => setModalMode("pause")}
              className="btn-secondary inline-flex items-center gap-2 text-[10px] border-accent-amber/60 text-accent-amber hover:bg-accent-amber hover:text-background"
            >
              Pause my account →
            </button>
          </div>
        )}

        <div className={`panel p-5 border-destructive/40 ${paused ? "md:col-span-2 xl:col-span-2" : ""}`}>
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-4 h-4 text-destructive" strokeWidth={1.5} />
            <div className="terminal-label text-destructive">Delete account</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Permanently erase your profile, files, generated materials, and history. Cannot be undone.
          </p>
          <button
            data-testid="delete-account-btn"
            onClick={() => setModalMode("delete")}
            className="btn-secondary inline-flex items-center gap-2 text-[10px] border-destructive/60 text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            Delete my account →
          </button>
        </div>
      </div>

      <EmailPreferencesModal open={emailPrefsOpen} onClose={() => setEmailPrefsOpen(false)} />

      <AccountActionModal
        mode={modalMode}
        open={modalMode !== null}
        onClose={() => !busy && setModalMode(null)}
        onSubmit={submitAction}
        busy={busy}
      />

      {celebrate && (
        <GotAJobCelebration onDone={onCelebrationDone} candidateName={celebrate.name} />
      )}
    </div>
  );
}

function Basics({ profile, save, busy }) {
  const [s, setS] = useState({
    name: profile.name, title: profile.title, location: profile.location, discipline: profile.discipline, bio: profile.bio,
    linkedin_url: profile.linkedin_url || "",
    behance_url: profile.behance_url || "",
    headshot_path: profile.headshot_path || null,
  });
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);

  const uploadHeadshot = async (file) => {
    setUploadingHeadshot(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/headshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((prev) => ({ ...prev, headshot_path: res.data.storage_path }));
      toast.success("Headshot uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploadingHeadshot(false); }
  };

  return (
    <div className="panel p-8 space-y-6">
      <div className="flex items-start gap-6">
        <label className="flex-shrink-0 cursor-pointer block" data-testid="upload-headshot">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadHeadshot(e.target.files[0])} disabled={uploadingHeadshot} />
          <div className="w-32 h-32 border border-foreground/40 overflow-hidden bg-surface flex items-center justify-center hover:border-primary transition-colors relative">
            {s.headshot_path ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.headshot_path}?auth=public`}
                alt="Headshot"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center px-2">
                {uploadingHeadshot ? "..." : "Upload headshot"}
              </span>
            )}
            {s.headshot_path && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/70 opacity-0 hover:opacity-100 transition-opacity">
                <span className="text-[10px] uppercase tracking-[0.2em] text-primary">Replace</span>
              </div>
            )}
          </div>
        </label>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="terminal-label">Name</label><input data-testid="profile-edit-name" className="input-base mt-2" value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} /></div>
          <div><label className="terminal-label">Title</label><input data-testid="profile-edit-title" className="input-base mt-2" value={s.title || ""} onChange={(e) => setS({ ...s, title: e.target.value })} /></div>
          <div><label className="terminal-label">Location</label><input className="input-base mt-2" value={s.location || ""} onChange={(e) => setS({ ...s, location: e.target.value })} /></div>
          <div>
            <label className="terminal-label">Discipline</label>
            <select className="input-base mt-2" value={s.discipline || ""} onChange={(e) => setS({ ...s, discipline: e.target.value })}>
              <option value="">--</option>
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div><label className="terminal-label">Bio</label><textarea className="input-base mt-2 min-h-[80px]" value={s.bio || ""} onChange={(e) => setS({ ...s, bio: e.target.value })} /></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-foreground/20">
        <div>
          <label className="terminal-label flex items-center gap-2"><Linkedin className="w-3 h-3" /> LinkedIn</label>
          <input data-testid="profile-edit-linkedin" className="input-base mt-2" value={s.linkedin_url} onChange={(e) => setS({ ...s, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/yourhandle" />
        </div>
        <div>
          <label className="terminal-label">Behance</label>
          <input data-testid="profile-edit-behance" className="input-base mt-2" value={s.behance_url} onChange={(e) => setS({ ...s, behance_url: e.target.value })} placeholder="https://behance.net/yourhandle" />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-foreground/20">
        <button data-testid="save-basics" disabled={busy} onClick={() => save(s)} className="btn-primary inline-flex items-center gap-2"><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}

function Experience({ profile, save, busy }) {
  const [hist, setHist] = useState(profile.work_history?.length ? profile.work_history : [{ company: "", role: "", start: "", end: "", description: "" }]);
  const [edu, setEdu] = useState(profile.education?.length ? profile.education : [{ school: "", degree: "", year: "" }]);
  const [skills, setSkills] = useState(profile.skills || []);
  const [newSkill, setNewSkill] = useState("");
  const [resumeBusy, setResumeBusy] = useState(false);

  const addSkill = () => {
    const v = newSkill.trim();
    if (!v) return;
    if (skills.includes(v)) { setNewSkill(""); return; }
    setSkills([...skills, v]);
    setNewSkill("");
  };

  const reupload = async (file) => {
    setResumeBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = res.data.parsed || {};
      if (p.work_history) setHist(p.work_history);
      if (p.education) setEdu(p.education);
      if (p.skills?.length) setSkills(Array.from(new Set([...skills, ...p.skills])));
      toast.success("Resume re-parsed. Review & save.");
    } catch { toast.error("Re-parse failed"); }
    finally { setResumeBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="panel p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="terminal-label">Re-upload resume</div>
            <p className="text-xs text-muted-foreground mt-1">Replaces work history, education & skills from a fresh PDF/DOCX.</p>
          </div>
          <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="reupload-resume">
            <Upload className="w-3 h-3" /> {resumeBusy ? "Parsing..." : "Upload"}
            <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && reupload(e.target.files[0])} />
          </label>
        </div>
      </div>

      <div className="panel p-6">
        <div className="terminal-label mb-3">Skills</div>
        <p className="text-xs text-muted-foreground mb-3">Add skills one at a time. The AI uses these to score job fit and generate tailored materials.</p>
        <div className="flex gap-2 mb-3">
          <input
            data-testid="skill-input"
            className="input-base flex-1"
            placeholder="e.g. Brand identity"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
          />
          <button data-testid="skill-add" onClick={addSkill} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {skills.length === 0 && <span className="text-xs text-muted-foreground">No skills yet.</span>}
          {skills.map((sk, i) => (
            <span key={`${sk}-${i}`} className="inline-flex items-center gap-2 px-3 py-1 border border-foreground/40 text-xs" data-testid={`skill-chip-${i}`}>
              {sk}
              <button onClick={() => setSkills(skills.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="panel p-6">
        <div className="terminal-label mb-4">Work history</div>
        <div className="space-y-3">
          {hist.map((w, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 panel p-3">
              <input className="input-base md:col-span-3" placeholder="Company" value={w.company} onChange={(e) => { const a = [...hist]; a[i] = { ...w, company: e.target.value }; setHist(a); }} />
              <input className="input-base md:col-span-3" placeholder="Role" value={w.role} onChange={(e) => { const a = [...hist]; a[i] = { ...w, role: e.target.value }; setHist(a); }} />
              <input className="input-base md:col-span-2" placeholder="Start" value={w.start} onChange={(e) => { const a = [...hist]; a[i] = { ...w, start: e.target.value }; setHist(a); }} />
              <input className="input-base md:col-span-2" placeholder="End" value={w.end} onChange={(e) => { const a = [...hist]; a[i] = { ...w, end: e.target.value }; setHist(a); }} />
              <button className="md:col-span-2 btn-secondary" onClick={() => setHist(hist.filter((_, j) => j !== i))}><X className="w-3 h-3 inline" /></button>
              <textarea className="input-base md:col-span-12 min-h-[60px]" placeholder="Description" value={w.description} onChange={(e) => { const a = [...hist]; a[i] = { ...w, description: e.target.value }; setHist(a); }} />
            </div>
          ))}
          <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setHist([...hist, { company: "", role: "", start: "", end: "", description: "" }])}><Plus className="w-3 h-3" /> Add</button>
        </div>
      </div>

      <div className="panel p-6">
        <div className="terminal-label mb-4">Education</div>
        <div className="space-y-3">
          {edu.map((ed, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 panel p-3">
              <input className="input-base md:col-span-5" placeholder="School" value={ed.school} onChange={(e) => { const a = [...edu]; a[i] = { ...ed, school: e.target.value }; setEdu(a); }} />
              <input className="input-base md:col-span-4" placeholder="Degree" value={ed.degree} onChange={(e) => { const a = [...edu]; a[i] = { ...ed, degree: e.target.value }; setEdu(a); }} />
              <input className="input-base md:col-span-2" placeholder="Year" value={ed.year} onChange={(e) => { const a = [...edu]; a[i] = { ...ed, year: e.target.value }; setEdu(a); }} />
              <button className="md:col-span-1 btn-secondary px-2" onClick={() => setEdu(edu.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button className="btn-secondary inline-flex items-center gap-2" onClick={() => setEdu([...edu, { school: "", degree: "", year: "" }])}><Plus className="w-3 h-3" /> Add</button>
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="save-experience" disabled={busy} onClick={() => save({ skills, work_history: hist.filter((w) => w.company || w.role), education: edu.filter((e) => e.school || e.degree) })} className="btn-primary inline-flex items-center gap-2"><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}

function Portfolio({ profile, save, busy }) {
  const [pieces, setPieces] = useState(profile.portfolio_pieces || []);
  const [url, setUrl] = useState("");
  const [scraping, setScraping] = useState(false);

  const scrapeUrl = async () => {
    if (!url.trim()) return;
    setScraping(true);
    try {
      const res = await api.post("/upload/portfolio-url", { url: url.trim() });
      const newPieces = res.data.parsed?.portfolio_pieces || [];
      setPieces([...pieces, ...newPieces]);
      toast.success(`Imported ${newPieces.length} pieces`);
      setUrl("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scrape failed");
    } finally { setScraping(false); }
  };

  return (
    <div className="space-y-6">
      <div className="panel p-6">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <div className="terminal-label">Import from URL</div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Paste your existing portfolio site. Claude scans it and adds pieces.</p>
        <div className="flex gap-2">
          <input
            data-testid="profile-portfolio-url"
            className="input-base flex-1"
            placeholder="yourname.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button data-testid="profile-portfolio-scrape" onClick={scrapeUrl} disabled={scraping || !url.trim()} className="btn-primary">
            {scraping ? "..." : "Scan"}
          </button>
        </div>
      </div>

      <PortfolioPieceEditor pieces={pieces} onChange={setPieces} dataTestidPrefix="profile-piece" />

      <div className="flex justify-end">
        <button data-testid="save-portfolio" disabled={busy} onClick={() => save({ portfolio_pieces: pieces.filter((p) => p.title || p.description) })} className="btn-primary inline-flex items-center gap-2"><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}

function Voice({ profile, setProfile }) {
  const [samples, setSamples] = useState(profile.writing_samples || []);
  const [voice, setVoice] = useState(profile.writing_voice_analysis);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!content || content.length < 30) { toast.error("Need 30+ chars"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title || "Untitled");
      fd.append("content", content);
      const res = await api.post("/upload/writing-sample", fd);
      setSamples([...samples, res.data.sample]);
      setVoice(res.data.writing_voice_analysis);
      setProfile({ ...profile, writing_samples: [...samples, res.data.sample], writing_voice_analysis: res.data.writing_voice_analysis });
      setTitle(""); setContent("");
      toast.success("Sample added");
    } catch (err) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/writing-sample/${id}`);
    setSamples(samples.filter((s) => s.id !== id));
  };

  return (
    <div className="space-y-6">
      {voice && (
        <div className="panel p-5 border-accent-green/40">
          <div className="terminal-label text-accent-green mb-3">[ VOICE.ANALYSIS ]</div>
          <div className="text-sm space-y-1 font-mono">
            <div><span className="text-muted-foreground">tone:</span> {voice.tone}</div>
            <div><span className="text-muted-foreground">vocab:</span> {voice.vocabulary}</div>
            <div><span className="text-muted-foreground">cadence:</span> {voice.sentence_structure}</div>
            <div className="pt-2 text-foreground/80">{voice.voice_summary}</div>
          </div>
        </div>
      )}
      <div className="panel p-5">
        <div className="terminal-label mb-3">Samples ({samples.length})</div>
        <div className="space-y-2">
          {samples.map((s) => (
            <div key={s.id} className="flex justify-between gap-3 panel p-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-medium">{s.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-1">{s.content.substring(0, 200)}</div>
              </div>
              <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="panel p-5">
        <input className="input-base mb-3" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea className="input-base min-h-[140px] mb-3" placeholder="Paste your writing..." value={content} onChange={(e) => setContent(e.target.value)} />
        <button data-testid="add-voice-sample" onClick={add} disabled={busy} className="btn-primary">Add sample</button>
      </div>
    </div>
  );
}

function Preferences({ profile, save, busy }) {
  const p = profile.preferences || {};
  const [s, setS] = useState({
    desired_roles: (p.desired_roles || []).join(", "),
    industries: (p.industries || []).join(", "),
    work_modes: p.work_modes || [],
    target_cities: p.target_cities || [],
    enjoy_factors: p.enjoy_factors || "",
    avoid_factors: p.avoid_factors || "",
  });
  const [newCity, setNewCity] = useState("");

  const toggle = (key, val) => {
    const arr = s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val];
    setS({ ...s, [key]: arr });
  };

  const addCity = () => {
    const v = newCity.trim();
    if (!v) return;
    if (s.target_cities.includes(v)) { setNewCity(""); return; }
    setS({ ...s, target_cities: [...s.target_cities, v] });
    setNewCity("");
  };

  return (
    <div className="panel p-8 space-y-5">
      <div><label className="terminal-label">Desired roles</label><input className="input-base mt-2" value={s.desired_roles} onChange={(e) => setS({ ...s, desired_roles: e.target.value })} /></div>
      <div><label className="terminal-label">Industries</label><input className="input-base mt-2" value={s.industries} onChange={(e) => setS({ ...s, industries: e.target.value })} /></div>

      <div>
        <label className="terminal-label">Target cities</label>
        <p className="text-xs text-muted-foreground mt-1 mb-2">Cities you want to work in. The AI factors these into the enjoyment score for each job.</p>
        <div className="flex gap-2">
          <input
            data-testid="city-input"
            className="input-base flex-1"
            placeholder="e.g. New York, London, Berlin"
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
          />
          <button data-testid="city-add" onClick={addCity} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {s.target_cities.length === 0 && <span className="text-xs text-muted-foreground">No cities yet.</span>}
          {s.target_cities.map((city, i) => (
            <span key={`${city}-${i}`} className="inline-flex items-center gap-2 px-3 py-1 border border-foreground/40 text-xs" data-testid={`city-chip-${i}`}>
              {city}
              <button onClick={() => setS({ ...s, target_cities: s.target_cities.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div>
        <label className="terminal-label">Work modes</label>
        <div className="flex gap-2 mt-2 flex-wrap">
          {["remote", "hybrid", "onsite"].map((m) => (
            <button key={m} onClick={() => toggle("work_modes", m)} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${s.work_modes.includes(m) ? "border-primary bg-primary text-primary-foreground" : "border-foreground/40 text-foreground hover:border-primary"}`}>{m}</button>
          ))}
        </div>
      </div>
      <div><label className="terminal-label">Enjoy factors</label><textarea className="input-base mt-2 min-h-[80px]" value={s.enjoy_factors} onChange={(e) => setS({ ...s, enjoy_factors: e.target.value })} /></div>
      <div><label className="terminal-label">Avoid factors</label><textarea className="input-base mt-2 min-h-[80px]" value={s.avoid_factors} onChange={(e) => setS({ ...s, avoid_factors: e.target.value })} /></div>
      <div className="flex justify-end pt-4 border-t border-foreground/20">
        <button data-testid="save-preferences" disabled={busy} onClick={() => save({ preferences: { desired_roles: s.desired_roles.split(",").map((x) => x.trim()).filter(Boolean), industries: s.industries.split(",").map((x) => x.trim()).filter(Boolean), work_modes: s.work_modes, target_cities: s.target_cities, enjoy_factors: s.enjoy_factors, avoid_factors: s.avoid_factors, company_sizes: [] } })} className="btn-primary inline-flex items-center gap-2"><Save className="w-3 h-3" /> Save</button>
      </div>
    </div>
  );
}
